import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { useT } from '../context/LangContext';

/**
 * JinglePlayer — minimal, accessible, click-to-play campaign audio.
 * - Never autoplays
 * - Audio element is lazy-mounted (only created after first user click)
 * - Animated lime → yellow waveform while playing; static bar when paused
 * - Honours prefers-reduced-motion (waveform freezes)
 */
const SRC = '/branding/tomorrow-we-grow.mp3';

export default function JinglePlayer() {
  const t = useT();
  const [mounted, setMounted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const reduceMotion = typeof window !== 'undefined' &&
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Wire up event listeners once the audio element exists
  useEffect(() => {
    if (!mounted || !audioRef.current) return;
    const a = audioRef.current;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
    };
  }, [mounted]);

  const toggle = () => {
    if (!mounted) {
      setMounted(true);
      // Defer the play call to the next tick so the <audio> element exists
      requestAnimationFrame(() => {
        if (audioRef.current) audioRef.current.play().catch(() => {});
      });
      return;
    }
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) {
      setMuted(!muted);
      return;
    }
    audioRef.current.muted = !audioRef.current.muted;
    setMuted(audioRef.current.muted);
  };

  // Build a static bar of 14 segments — animate when playing (CSS keyframes)
  const bars = Array.from({ length: 14 });

  return (
    <div
      className="flex items-center gap-4 p-4 rounded-2xl border border-[#07743F]/15 bg-white/70 backdrop-blur-sm"
      data-testid="jingle-player"
      aria-label={t('jingle.aria', 'Tomorrow We Grow — the YEFFA anthem')}
    >
      <button
        type="button"
        onClick={toggle}
        aria-pressed={playing}
        aria-label={playing ? t('jingle.pause', 'Pause jingle') : t('jingle.play', 'Play jingle')}
        data-testid="jingle-play-btn"
        className="relative flex-shrink-0 w-12 h-12 rounded-full bg-[#E4E517] hover:bg-[#d4d514] active:scale-95 transition flex items-center justify-center text-[#07743F] shadow-sm"
      >
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#72BF44] mb-1">
          {t('jingle.eyebrow', 'Hear it')}
        </div>
        <div className="text-sm font-serif text-gray-900 truncate">
          {t('jingle.title', 'This is what tomorrow sounds like')}
        </div>
      </div>

      {/* Waveform bars */}
      <div
        className="hidden sm:flex items-end gap-[3px] h-8"
        aria-hidden="true"
        data-testid="jingle-waveform"
      >
        {bars.map((_, i) => (
          <span
            key={i}
            className={
              'w-[3px] rounded-full transition-all duration-300 ' +
              (playing && !reduceMotion ? 'jingle-bar-anim' : '')
            }
            style={{
              height: playing && !reduceMotion ? undefined : (8 + (i % 3) * 4) + 'px',
              animationDelay: (i * 70) + 'ms',
              background: 'linear-gradient(180deg, #72BF44 0%, #E4E517 100%)',
            }}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? t('jingle.unmute', 'Unmute') : t('jingle.mute', 'Mute')}
        data-testid="jingle-mute-btn"
        className="flex-shrink-0 w-9 h-9 rounded-full border border-gray-200 hover:border-[#07743F]/40 text-gray-500 hover:text-[#07743F] transition flex items-center justify-center"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>

      {/* Audio element is mounted lazily — first click creates it */}
      {mounted && (
        <audio
          ref={audioRef}
          src={SRC}
          preload="none"
          data-testid="jingle-audio"
        />
      )}

      <style>{`
        @keyframes jingleBar {
          0%, 100% { height: 6px; }
          50% { height: 28px; }
        }
        .jingle-bar-anim {
          animation: jingleBar 1s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .jingle-bar-anim { animation: none; }
        }
      `}</style>
    </div>
  );
}
